import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSlice, createSelector, current } from '@reduxjs/toolkit';
import {
  transformInitialEvaluationValuesFood,
  transformInitialEvaluationValuesNonFood,
  transformInitialEvaluationValuesMultiPack,
} from 'pages/new-evaluation-form/utils/mappingValueEvaluationForm';
import {
  isAllergensNonFoodForm,
  isFoodForm,
  isNonFoodForm,
  isMultiPackFoodForm,
} from 'pages/new-evaluation-form/utils/checkTypeEvaluationForm';
import { getChangedHeaderNamePhotoLabelEvalForm } from 'pages/new-evaluation-form/utils/getChangedEvaluationForm';
import { mappingReadOnlyAllergens } from 'pages/new-evaluation-form/utils/validateSubmitEvalForm';

const initialState = {
  dataTable: {
    food: {},
    'non-food': {},
    multipack: {},
    readOnlyAllergens: {},
  },
  initialDataTable: {
    food: {},
    'non-food': {},
    multipack: {},
    readOnlyAllergens: {},
  },
  statusChangeFormValues: 'idle',
  nonFoodSpecificationAllergens: [],
};

export const useGetEvalFormType = type => {
  return useSelector(selectEvaluationForm)?.dataTable?.[type?.toLowerCase()] ?? {};
};

export const useGetEvalFormTableType = ({ type, nameTable }) => {
  return useSelector(
    selectEvaluationFormByType(type, nameTable)
  );
};

export const useGetInitialEvalFormTableType = ({ type, nameTable }) => {
  return useSelector(
    selectInitialEvaluationFormByType(type, nameTable)
  );
};

export const useGetNonFoodTable = nameTable => {
  const evaluationFormData = useSelector(selectEvaluationForm) ?? {};

  return useMemo(
    () => evaluationFormData?.nonFoodTable?.[nameTable] ?? [],
    [nameTable, evaluationFormData]
  );
};

export const useGetReadOnlyAllergensNonFood = () => {
  return useSelector(selectReadOnlyAllergens()) ?? {};
};

export const useGetQaEvaluationForm = () => {
  return useSelector(selectEvaluationForm) ?? {};
};

export const useGetSpecificProductImagery = ({
  type,
  nameTable,
  propertyName,
}) => {
  const productImagery = useGetEvalFormTableType({ type, nameTable });

  return useMemo(
    () => productImagery[propertyName] ?? [],
    [productImagery, propertyName]
  );
};

export const useGetProductImagery = type => {
  return useSelector(selectProductImageryFood(type));
};

export const useGetNonFoodSpecificationAllergens = () => {
  return useSelector(selectNonFoodSpecificationAllergens());
};

export const evaluationFormSlice = createSlice({
  name: 'evaluationForm',
  initialState,
  reducers: {
    initialEvaluationForm: (state, action) => {
      const { type, values } = action?.payload ?? {};
      const qaEvaluationFormDetail = values?.qaEvaluationFormDetail ?? {};
      const qaEvaluationFormAssetDetails = values?.qaEvaluationFormAssetDetails ?? [];

      if (isFoodForm(type)) {
        const mappingInitialValues = transformInitialEvaluationValuesFood(
          qaEvaluationFormDetail,
          qaEvaluationFormAssetDetails
        );

        state.dataTable[type?.toLowerCase()] = mappingInitialValues;
        state.initialDataTable[type?.toLowerCase()] = mappingInitialValues;
        state.statusChangeFormValues = 'idle';
      } else if (isNonFoodForm(type)) {
        const mappingInitialValues = transformInitialEvaluationValuesNonFood(
          qaEvaluationFormDetail,
          qaEvaluationFormAssetDetails
        );

        state.dataTable['non-food'] = mappingInitialValues;
        state.initialDataTable['non-food'] = mappingInitialValues;
        state.initialDataTable.readOnlyAllergens = mappingReadOnlyAllergens(values?.readOnlyAllergens) ?? {};
        state.statusChangeFormValues = 'idle';
      } else if (isMultiPackFoodForm(type)) {
        const qaEvaluationFormMultiPackDetail = values?.qaEvaluationFormMultiPackDetail ?? {};
        const mappingInitialValues = transformInitialEvaluationValuesMultiPack(
          qaEvaluationFormMultiPackDetail,
          qaEvaluationFormAssetDetails
        );

        state.dataTable[type?.toLowerCase()] = mappingInitialValues;
        state.initialDataTable[type?.toLowerCase()] = mappingInitialValues;
        state.statusChangeFormValues = 'idle';
      }
    },
    updateEvaluationForm: (state, action) => {
      const {
        type,
        nameTable,
        photoLabel = false,
        indexOfFields,
        changedValue,
      } = action.payload;

      if (photoLabel) {
        const beforeTableData = current(state.dataTable[type][nameTable][0]);
        const [propertyName, index, changedProperty] = indexOfFields;
        const newTableSource = getChangedHeaderNamePhotoLabelEvalForm({
          tableDataSource: beforeTableData,
          name: indexOfFields,
          changedValue,
        });
        const beforeValue = beforeTableData?.[propertyName]?.[index]?.[changedProperty];
        const afterValue = newTableSource?.[0]?.[propertyName]?.[index]?.[changedProperty];

        if (beforeValue !== afterValue) {
          const [propertyName, index, changedProperty] = indexOfFields;

          state.dataTable[type][nameTable][0][propertyName][index][changedProperty] = changedValue;
          state.statusChangeFormValues = 'changed';
        }
      } else {
        const readOnlyAllergens = current(
          state.initialDataTable.readOnlyAllergens
        );

        // Multipack-aware logic: if indexOfFields[0] is a number, treat as tableIndex
        let tableIndex = indexOfFields[0];
        let propertyName, changedProperty;
        let beforeTableData, foundIndex;
        if (typeof tableIndex === 'number') {
          // Multipack: [tableIndex, propertyName, changedProperty]
          propertyName = indexOfFields[1];
          changedProperty = indexOfFields[2];
          beforeTableData = current(state.dataTable[type][nameTable][tableIndex]);

          foundIndex = beforeTableData.findIndex(item => item.property.name === propertyName);
          if (foundIndex !== -1) {
            state.dataTable[type][nameTable][tableIndex][foundIndex][changedProperty] = changedValue;
            state.statusChangeFormValues = 'changed';
          }
        } else {
          // Fallback: non-multipack (legacy) logic
          propertyName = indexOfFields[0];
          changedProperty = indexOfFields[indexOfFields.length - 1];
          beforeTableData = current(state.dataTable[type][nameTable]);

          foundIndex = beforeTableData.findIndex(item => item.property.name === propertyName);
          if (foundIndex !== -1) {
            state.dataTable[type][nameTable][foundIndex][changedProperty] = changedValue;
            state.statusChangeFormValues = 'changed';
          }
        }

        // Allergen non-food logic (unchanged)
        if (
          isAllergensNonFoodForm(type, nameTable) &&
          changedProperty === 'headerName'
        ) {
          state.dataTable[type][nameTable][foundIndex]['specification'] = readOnlyAllergens[changedValue?.toLowerCase()] ?? '';
        }
      }
    },
    setOrganolepticSummaryData: (state, action) => {
      const {
        tableIndex,
        updatedSummary,
        type = 'multipack',
      } = action.payload;
      const nameTable = 'organolepticEvaluationSummary';

      // 1. Defensive check
      if (!state.dataTable?.[type]?.[nameTable] || state.dataTable[type][nameTable][tableIndex] === undefined) {
        console.error('Redux state path not found for Organoleptic Summary update.');

        return;
      }

      state.dataTable[type][nameTable][tableIndex] = updatedSummary;
      state.statusChangeFormValues = 'changed';
    },
    updateOrganolepticForm: (state, action) => {
      const {
        type,
        nameTable,
        photoLabel = false,
        indexOfFields,
        changedValue,
      } = action.payload;

      // 🧩 Special case: Organoleptic Findings nested updates
      if (
        nameTable === 'organolepticEvaluationSummary' &&
        Array.isArray(indexOfFields) &&
        indexOfFields[0] === 'qaEvalOrganolepticFinding'
      ) {
        const table = state.dataTable?.[type]?.[nameTable];

        if (!Array.isArray(table)) return;

        const idx = table.findIndex(item => item.property?.name === 'qaEvalOrganolepticFinding');

        if (idx === -1) return;

        // Replace the entire qaEvalOrganolepticFinding object
        if (indexOfFields.length === 1) {
          table[idx] = {
            ...table[idx],
            ...changedValue,
          };

          state.statusChangeFormValues = 'changed';

          return;
        }

        // Update a property inside qaEvalOrganolepticFinding
        const propertyName = indexOfFields[1];

        table[idx] = {
          ...table[idx],
          [propertyName]: changedValue,
        };

        state.statusChangeFormValues = 'changed';

        return;
      }

      // 🧩 Default (non-photo) field updates
      if (!photoLabel) {
        const table = state.dataTable?.[type]?.[nameTable];

        if (!Array.isArray(table)) return;

        const foundIndex = table.findIndex(item => item.property?.name === indexOfFields[0]);

        if (foundIndex !== -1) {
          table[foundIndex] = {
            ...table[foundIndex],
            [indexOfFields[1]]: changedValue,
          };

          state.statusChangeFormValues = 'changed';
        }

        return;
      }

      // 🧩 Handle photoLabel (legacy image header path)
      if (photoLabel) {
        const [propertyName, index, changedProperty] = indexOfFields;
        const table = state.dataTable?.[type]?.[nameTable];

        if (
          Array.isArray(table) &&
          table[0]?.[propertyName]
        ) {
          table[0][propertyName][index][changedProperty] = changedValue;

          state.statusChangeFormValues = 'changed';
        }
      }
    },
    uploadHeaderImage: (state, action) => {
      const { type, imageObject, propertyName } = action.payload;

      state.dataTable[type].headerImage[0][propertyName] = imageObject;
      state.statusChangeFormValues = 'changed';
    },
    uploadHeaderImageMultipack: (state, action) => {
      const { type, imageObject, propertyName, tableIndex } = action.payload;

      state.dataTable[type].headerImage[tableIndex][0][propertyName] = imageObject;
      state.statusChangeFormValues = 'changed';
    },
    uploadProductImagery: (state, action) => {
      const { type, imageObject, nameTable, propertyName } = action.payload;
      const [parentPropertyName, childPropertyName] = propertyName;

      state.dataTable[type][nameTable][0][parentPropertyName][childPropertyName]['digitalAssetId'] = imageObject;
      state.statusChangeFormValues = 'changed';
    },
    updateStatusChangeFormValues: (state, action) => {
      const { status } = action.payload;

      state.statusChangeFormValues = status;
    },
    updateNonFoodSpecificationAllergens: (state, action) => {
      const { index, value } = action.payload;

      state.nonFoodSpecificationAllergens[index] = value;
    },
    updateSensorySummaryScores: (state, action) => {
      const {
        type,
        parameter,
        privateScore,
        competitiveScore,
      } = action.payload;
      const table = state.dataTable?.[type]?.sensoryEvaluationSummary;

      if (!Array.isArray(table)) return;

      const rowIndex = table.findIndex(item => item.property?.name === parameter);

      if (rowIndex === -1) return;

      table[rowIndex].privateBrandProductScores = privateScore;
      table[rowIndex].competitiveBrandProductScores = competitiveScore;

      state.statusChangeFormValues = 'changed';
    },
    updateSensorySummaryFromAverages: (state, action) => {
      const { type, averages } = action.payload;
      const table = state.dataTable?.[type]?.sensoryEvaluationSummary;

      if (!Array.isArray(table)) return;

      table.forEach(row => {
        const name = row.property?.name;

        switch (name) {
          case 'visual':
            row.privateBrandProductScores = averages.visualPrivateBrandScore;
            row.competitiveBrandProductScores = averages.visualCompetitiveBrandScore;

            break;
          case 'workmanship':
            row.privateBrandProductScores = averages.workmanshipPrivateBrandScore;
            row.competitiveBrandProductScores = averages.workmanshipCompetitiveBrandScore;

            break;
          case 'function':
            row.privateBrandProductScores = averages.functionPrivateBrandScore;
            row.competitiveBrandProductScores = averages.functionCompetitiveBrandScore;

            break;
          case 'aroma':
            row.privateBrandProductScores = averages.aromaPrivateBrandScore;
            row.competitiveBrandProductScores = averages.aromaCompetitiveBrandScore;

            break;
          case 'aroma':
            row.privateBrandProductScores = averages.aromaPrivateBrandScore;
            row.competitiveBrandProductScores = averages.aromaCompetitiveBrandScore;

            break;
          case 'flavor':
            row.privateBrandProductScores = averages.flavorPrivateBrandScore;
            row.competitiveBrandProductScores = averages.flavorCompetitiveBrandScore;

            break;
          case 'texture':
            row.privateBrandProductScores = averages.texturePrivateBrandScore;
            row.competitiveBrandProductScores = averages.textureCompetitiveBrandScore;

            break;
          case 'overall':
            row.privateBrandProductScores = averages.overallPrivateBrandScore;
            row.competitiveBrandProductScores = averages.overallCompetitiveBrandScore;

            break;
        }
      });

      state.statusChangeFormValues = 'changed';
    },
    updateSensoryFindingAverages: (state, action) => {
      const { type, averages } = action.payload;
      const table = state.dataTable[type].sensoryEvaluationSummary;
      const index = table.findIndex(item => item.property?.name === 'qaEvalSensoryFinding');

      if (index === -1) return;

      table[index] = {
        ...table[index],
        ...averages,
      };
    },
  },
});

export const selectEvaluationForm = createSelector(
  (state) => state,
  (state) => state?.evaluationForm
);

export const selectEvaluationFormByType = (type, nameTable) => createSelector(
  selectEvaluationForm,
  (state) => state?.dataTable?.[type?.toLowerCase()]?.[nameTable] ?? []
);

export const selectInitialEvaluationFormByType = (type, nameTable) => createSelector(
  selectEvaluationForm,
  (state) => state?.initialDataTable?.[type?.toLowerCase()]?.[nameTable] ?? []
);

export const selectReadOnlyAllergens = () => createSelector(
  selectEvaluationForm,
  (state) => state?.initialDataTable?.readOnlyAllergens ?? {}
);

export const selectProductImageryFood = (type) => createSelector(
  selectEvaluationForm,
  (state) => state?.dataTable?.[type.toLowerCase()]?.productImagery
);

export const selectProductImageryNonFood = () => createSelector(
  selectEvaluationForm,
  (state) => state?.dataTable?.nonfood?.productImagery
);

export const selectStatusChangeFormValues = () => createSelector(
  selectEvaluationForm,
  (state) => state?.statusChangeFormValues
);

export const selectNonFoodSpecificationAllergens = () => createSelector(
  selectEvaluationForm,
  (state) => state?.nonFoodSpecificationAllergens
);

export const {
  updateEvaluationForm,
  updateOrganolepticForm,
  setOrganolepticSummaryData,
  uploadHeaderImage,
  uploadHeaderImageMultipack,
  uploadProductImagery,
  initialEvaluationForm,
  updateStatusChangeFormValues,
  updateNonFoodSpecificationAllergens,
  updateSensorySummaryScores,
  updateSensorySummaryFromAverages,
  updateSensoryFindingAverages
} = evaluationFormSlice.actions;

export default evaluationFormSlice.reducer;